import Select from "react-select";
import * as APIModels from "../../../models";

type PartyOption = { label: string; value: string };

// Identifier used to reference a party in the filing-party selection. Prefers the
// court participant id (guid): the API pre-selects filing parties by participant id,
// and a row id only exists once the party is saved. The API accepts either.
export const filingPartyKey = (p: APIModels.Party): string | undefined =>
    p.guid ?? p.id;

// Multi-select of the case's parties (attorneys excluded) for choosing the filing
// parties, shown next to the case's "Filing Party Responsible" text so the
// processor can match them up. Below it, "Primary Party" picks the one party a
// document is specifically for (Submission.filingPartyID). The two are independent
// fields used for different things, so each keeps its own value.
export default function FilingPartiesSelect({
    parties,
    selectedIds,
    onChange,
    filingPartyResponsible,
    primaryId,
    onPrimaryChange,
    onAddParty,
}: {
    parties: APIModels.Party[];
    selectedIds: string[];
    onChange: (ids: string[]) => void;
    filingPartyResponsible?: string;
    primaryId?: string;
    onPrimaryChange: (id: string | undefined) => void;
    onAddParty?: () => void;
}) {
    const options: PartyOption[] = parties
        .filter(
            (p) =>
                !p.isLeadAttorney &&
                (p.partyType ?? "").indexOf("Attorney") === -1 &&
                filingPartyKey(p)
        )
        .map((p) => {
            const name =
                p.name && p.name.trim() !== ""
                    ? p.name
                    : `${p.firstName ?? ""} ${p.lastName ?? ""}`.trim();
            return {
                value: filingPartyKey(p) as string,
                label: `${p.partyType ? p.partyType + " - " : ""}${name}`,
            };
        });

    return (
        <>
            <div className="row mt-3">
                <div className="col-sm-7">
                    <label htmlFor="selFilingParties" className="form-label">
                        Filing Parties
                    </label>
                    <Select
                        inputId="selFilingParties"
                        isMulti
                        options={options}
                        value={options.filter((o) =>
                            selectedIds.includes(o.value)
                        )}
                        onChange={(value: any) =>
                            onChange(
                                ((value as PartyOption[]) ?? []).map(
                                    (o) => o.value
                                )
                            )
                        }
                        placeholder="Select the filing party(s)"
                        noOptionsMessage={() => "No parties on this case"}
                    />
                </div>
                <div className="col-sm-5">
                    <label
                        htmlFor="txtFilingPartyResponsible"
                        className="form-label"
                    >
                        Filing Party Responsible
                    </label>
                    <input
                        id="txtFilingPartyResponsible"
                        className="form-control"
                        type="text"
                        value={filingPartyResponsible ?? ""}
                        readOnly
                    />
                </div>
            </div>
            <div className="row mt-3">
                <div className="col-sm-7">
                    <label htmlFor="selPrimaryParty" className="form-label">
                        Primary Party
                    </label>
                    <Select
                        inputId="selPrimaryParty"
                        isClearable
                        options={options}
                        value={
                            options.find((o) => o.value === primaryId) ?? null
                        }
                        onChange={(value: any) =>
                            onPrimaryChange(
                                (value as PartyOption | null)?.value
                            )
                        }
                        placeholder="Select the primary party"
                        noOptionsMessage={() => "No parties on this case"}
                    />
                </div>
                {onAddParty && (
                    <div className="col-sm-5 d-flex align-items-end">
                        <button
                            type="button"
                            className="btn btn-primary"
                            onClick={onAddParty}
                        >
                            Add Filing Party
                        </button>
                    </div>
                )}
            </div>
        </>
    );
}
